import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TransactionStatus, TransactionType } from "../utils/enums";
import { parseApiUtcToLocal } from "../utils/dateTime";
import { openSupportChatOnCurrentPage } from "../utils/supportChatNavigation";
import AppConstants from "../utils/appConstants";
import { resolveInternalTransferParties } from "../utils/transactionAccountId";
import "./css/TransactionDetailsModal.css";

function parseLocalizedLabel(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    return (
      value.en ||
      value.EN ||
      value.ar ||
      value.METHOD_NAME ||
      value.NAME ||
      null
    );
  }
  const text = String(value).trim();
  if (!text) return null;
  if (text.startsWith("{")) {
    try {
      const obj = JSON.parse(text);
      if (obj && typeof obj === "object") {
        return obj.en || obj.EN || obj.ar || Object.values(obj)[0] || text;
      }
    } catch (_) {
      return text;
    }
  }
  return text;
}

function formatAmountNumber(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatBreakdownAmount(value, symbol, code) {
  const formatted = formatAmountNumber(value);
  if (!formatted) return "-";
  const mark = String(symbol || "").trim();
  if (mark && !/[A-Za-z]{2,}/.test(mark)) return `${mark}${formatted}`;
  if (String(code || "").toUpperCase() === "USD") return `$${formatted}`;
  const suffix = String(code || mark || "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
}

function formatHeroCurrency(symbol, code) {
  const currencyCode = String(code || "").trim();
  if (currencyCode) return currencyCode;
  const mark = String(symbol || "").trim();
  return mark || "";
}

function formatReceiptDate(value) {
  const date = parseApiUtcToLocal(value);
  if (!date) return null;
  try {
    const datePart = date
      .toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
      .replace(/\./g, "")
      .toUpperCase();
    const timePart = date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${datePart} • ${timePart}`;
  } catch (_) {
    return String(value);
  }
}

function getTypeTone(typeCode, typeClass) {
  const numericType = Number(typeCode);
  if (typeClass === "type-cashin" || numericType === TransactionType.CASHIN || numericType === TransactionType.CASHIN_MT5) {
    return "cashin";
  }
  if (typeClass === "type-cashout" || numericType === TransactionType.CASHOUT) {
    return "cashout";
  }
  if (
    typeClass === "type-transfer" ||
    numericType === TransactionType.INTERNAL_TRANSFER ||
    numericType === TransactionType.TRANSFER
  ) {
    return "transfer";
  }
  return "default";
}

function getStatusTone(statusCode, statusLabel) {
  const numericStatus = Number(statusCode);
  if (
    numericStatus === TransactionStatus.COMPLETED ||
    numericStatus === TransactionStatus.COLLECTED ||
    numericStatus === TransactionStatus.TRANSFERRED
  ) {
    return "completed";
  }
  if (
    numericStatus === TransactionStatus.PENDING ||
    numericStatus === TransactionStatus.PROGRESSING ||
    numericStatus === TransactionStatus.STARTED ||
    numericStatus === TransactionStatus.PENDING_USER
  ) {
    return "pending";
  }
  if (
    numericStatus === TransactionStatus.FAILED ||
    numericStatus === TransactionStatus.REJECTED ||
    numericStatus === TransactionStatus.CANCELED
  ) {
    return "failed";
  }

  const label = String(statusLabel || "").toLowerCase();
  if (
    label.includes("complete") ||
    label.includes("success") ||
    label.includes("collected") ||
    label.includes("transferred")
  ) {
    return "completed";
  }
  if (label.includes("pending") || label.includes("wait") || label.includes("progress") || label.includes("start")) {
    return "pending";
  }
  if (label.includes("fail") || label.includes("reject") || label.includes("cancel")) {
    return "failed";
  }
  return "pending";
}

function extractPaymentMethod(raw) {
  if (!raw || typeof raw !== "object") return null;
  const requestRaw = raw.transaction_request || raw.TRANSACTION_REQUEST;
  const request = requestRaw && typeof requestRaw === "object" ? requestRaw : {};
  const methodObject =
    raw.payment_methods ||
    raw.PAYMENT_METHOD ||
    raw.PaymentMethod ||
    request.payment_methods ||
    request.PAYMENT_METHOD ||
    null;

  return (
    parseLocalizedLabel(raw.METHOD_LABEL) ||
    parseLocalizedLabel(raw.METHOD_NAME) ||
    parseLocalizedLabel(raw.PAYMENT_METHOD_LABEL) ||
    parseLocalizedLabel(raw.PAYMENT_METHOD_NAME) ||
    parseLocalizedLabel(methodObject?.METHOD_LABEL) ||
    parseLocalizedLabel(methodObject?.METHOD_NAME) ||
    parseLocalizedLabel(methodObject?.NAME) ||
    parseLocalizedLabel(request.METHOD_LABEL) ||
    parseLocalizedLabel(request.METHOD_NAME) ||
    null
  );
}

function extractCurrencyMeta(detailsData, detailsTx) {
  const raw = detailsData?.raw || detailsTx?.raw || {};
  const currencyObj =
    raw.currencies ||
    raw.CURRENCY ||
    raw.currency ||
    null;
  return {
    symbol:
      detailsData?.currencySymbol ||
      currencyObj?.CURRENCY_SYMBOL ||
      currencyObj?.currencySymbol ||
      currencyObj?.SYMBOL ||
      raw.CURRENCY_SYMBOL ||
      "",
    code:
      detailsData?.currencyCode ||
      currencyObj?.CURRENCY_CODE ||
      currencyObj?.currencyCode ||
      currencyObj?.CODE ||
      raw.CURRENCY_CODE ||
      "",
  };
}

const TYPE_ICONS = {
  cashin: "fa-money-bill-wave",
  cashout: "fa-money-bill-transfer",
  transfer: "fa-right-left",
  default: "fa-receipt",
};

const STATUS_ICONS = {
  completed: "fa-check",
  pending: "fa-clock",
  failed: "fa-xmark",
};

const TransactionDetailsModal = ({
  isOpen,
  onClose,
  detailsTx,
  detailsData,
  detailsLoading,
  detailsError,
  actions = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const view = useMemo(() => {
    const typeCode = detailsData?.typeCode ?? detailsTx?.typeCode;
    const typeClass = detailsTx?.typeClass;
    const typeTone = getTypeTone(typeCode, typeClass);
    const statusCode = detailsData?.statusCode ?? detailsTx?.statusCode;
    const statusLabel = detailsData?.statusLabel || detailsTx?.statusLabel || "";
    const statusTone = getStatusTone(statusCode, statusLabel);
    const raw = detailsData?.raw || detailsTx?.raw || {};
    const currency = extractCurrencyMeta(detailsData, detailsTx);
    const amount = detailsData?.amount ?? detailsTx?.amount;
    const fees =
      detailsData?.adminFees != null || detailsData?.agentFees != null
        ? (Number(detailsData.adminFees) || 0) + (Number(detailsData.agentFees) || 0)
        : 0;
    const received =
      detailsData?.amountAfterFees != null
        ? detailsData.amountAfterFees
        : amount != null
        ? Number(amount) - fees
        : null;
    const typeLabel =
      detailsTx?.typeLabel ||
      detailsData?.typeLabel ||
      t("transaction", { ns: "mobile", defaultValue: "Transaction" });
    const paymentMethod =
      detailsData?.paymentMethod ||
      extractPaymentMethod(raw) ||
      (typeTone === "transfer" ? typeLabel : null) ||
      "-";

    const statusTitleKey = {
      completed: "transaction_completed",
      pending: "transaction_pending",
      failed: "transaction_failed",
    }[statusTone];

    const heroSign = typeTone === "cashout" ? "-" : typeTone === "cashin" ? "+" : "";
    const isInternalTransfer =
      typeTone === "transfer" ||
      Number(typeCode) === TransactionType.INTERNAL_TRANSFER ||
      Number(typeCode) === TransactionType.TRANSFER;

    const transferParties = isInternalTransfer
      ? resolveInternalTransferParties(raw, detailsData)
      : null;
    const senderName = transferParties
      ? transferParties.senderValue
      : detailsData?.senderName ||
        detailsData?.senderWalletLabel ||
        (typeTone === "cashin" ? AppConstants.adminNickName : null) ||
        "-";
    const receiverName = transferParties
      ? transferParties.receiverValue
      : detailsData?.receiverName ||
        detailsData?.receiverWalletLabel ||
        (typeTone === "cashout" ? AppConstants.adminNickName : null) ||
        "-";
    const senderLabel = transferParties
      ? t(transferParties.senderLabelKey, {
          ns: "mobile",
          defaultValue: transferParties.senderLabelDefault,
        })
      : t("sender", { ns: "mobile" });
    const receiverLabel = transferParties
      ? t(transferParties.receiverLabelKey, {
          ns: "mobile",
          defaultValue: transferParties.receiverLabelDefault,
        })
      : t("receiver", { ns: "mobile" });
    const receiverIcon = transferParties ? transferParties.receiverIcon : "fa-user";
    const senderIcon = transferParties ? transferParties.senderIcon : "fa-building-columns";

    return {
      typeTone,
      statusTone,
      typeLabel,
      paymentMethod,
      transactionId: detailsData?.id || detailsTx?.id || "-",
      dateLabel:
        formatReceiptDate(detailsData?.transactionDate || detailsTx?.date) || "-",
      statusTitle: t(statusTitleKey, {
        ns: "mobile",
        defaultValue:
          statusTone === "completed"
            ? "Transaction Completed"
            : statusTone === "failed"
            ? "Transaction Failed"
            : "Transaction Pending",
      }),
      heroSign,
      heroAmount: formatAmountNumber(amount) || "0.00",
      heroCurrency: formatHeroCurrency(currency.symbol, currency.code),
      totalAmount: formatBreakdownAmount(amount, currency.symbol, currency.code),
      feesAmount: formatBreakdownAmount(fees, currency.symbol, currency.code),
      receivedAmount: formatBreakdownAmount(received, currency.symbol, currency.code),
      netLabel:
        typeTone === "cashout"
          ? t("sent_amount", { ns: "mobile", defaultValue: "Sent Amount" })
          : t("received_amount", { ns: "mobile" }),
      senderName,
      senderLabel,
      receiverName,
      receiverLabel,
      receiverIcon,
      senderIcon,
    };
  }, [detailsData, detailsTx, t]);

  if (!isOpen) return null;

  const handleHelp = () => {
    const transactionNumber =
      view.transactionId && view.transactionId !== "-" ? String(view.transactionId) : "";
    const message = transactionNumber
      ? t("transaction_help_chat_message", {
          ns: "mobile",
          transactionId: transactionNumber,
          defaultValue: "Hello, I need assistance with the following transaction.\nTransaction ID: {{transactionId}}",
        })
      : t("transaction_help_chat_message_plain", {
          ns: "mobile",
          defaultValue: "Hello, I need assistance with a transaction.",
        });
    onClose?.();
    openSupportChatOnCurrentPage(navigate, location.pathname, location.search, message);
  };

  return (
    <div className="tdm-overlay" onClick={onClose} role="presentation">
      <div
        className={`tdm-modal tdm--${view.typeTone} tdm-status--${view.statusTone}`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tdm-title"
      >
        <div className="tdm-header">
          <div className="tdm-header-left">
            <span className="tdm-header-badge" aria-hidden="true">
              <i className="fa-solid fa-receipt" />
            </span>
            <h2 id="tdm-title" className="tdm-title">
              {t("transaction_details", { ns: "mobile" })}
            </h2>
          </div>
          <button type="button" className="tdm-close" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="tdm-body">
          {detailsLoading && (
            <div className="tdm-state">
              <span className="tdm-spinner" aria-hidden="true" />
              {t("loading", { ns: "mobile" })}
            </div>
          )}

          {!detailsLoading && detailsError && (
            <div className="tdm-state tdm-state--error">
              <span>{t("error", { ns: "mobile" })}</span>
              <strong>{detailsError}</strong>
            </div>
          )}

          {!detailsLoading && !detailsError && (detailsData || detailsTx) && (
            <>
              <div className="tdm-hero">
                <div className="tdm-status-ring" aria-hidden="true">
                  <i className={`fa-solid ${STATUS_ICONS[view.statusTone] || STATUS_ICONS.pending}`} />
                </div>
                <p className="tdm-status-label">{view.statusTitle}</p>
                <div className="tdm-amount">
                  <span className="tdm-amount-value">
                    {view.heroSign}
                    {view.heroAmount}
                  </span>
                  {view.heroCurrency ? (
                    <span className="tdm-amount-currency">{view.heroCurrency}</span>
                  ) : null}
                </div>
                <p className="tdm-date">
                  <i className="fa-regular fa-calendar" aria-hidden="true" />
                  {view.dateLabel}
                </p>
              </div>

              <div className="tdm-meta">
                <div className="tdm-meta-row">
                  <span>{t("transaction_id", { ns: "mobile" })}</span>
                  <strong>{view.transactionId}</strong>
                </div>
                <div className="tdm-meta-row">
                  <span>{t("type", { ns: "mobile" })}</span>
                  <strong className="tdm-type-value">
                    <i className={`fa-solid ${TYPE_ICONS[view.typeTone] || TYPE_ICONS.default}`} aria-hidden="true" />
                    {view.typeLabel}
                  </strong>
                </div>
                <div className="tdm-meta-row">
                  <span>{t("payment_method", { ns: "mobile" })}</span>
                  <strong>{view.paymentMethod}</strong>
                </div>
              </div>

              <div className="tdm-breakdown">
                <div className="tdm-breakdown-row">
                  <span>{t("total_amount", { ns: "mobile", defaultValue: "Total Amount" })}</span>
                  <strong>{view.totalAmount}</strong>
                </div>
                <div className="tdm-breakdown-row">
                  <span>{t("platform_fees", { ns: "mobile", defaultValue: "Platform Fees" })}</span>
                  <strong className="tdm-fees">{view.feesAmount}</strong>
                </div>
                <div className="tdm-breakdown-row tdm-breakdown-row--net">
                  <span>{view.netLabel}</span>
                  <strong>{view.receivedAmount}</strong>
                </div>
              </div>

              <div className="tdm-parties">
                <div className="tdm-party">
                  <span className="tdm-party-icon tdm-party-icon--sender" aria-hidden="true">
                    <i className={`fa-solid ${view.senderIcon}`} />
                  </span>
                  <div className="tdm-party-copy">
                    <span>{view.senderLabel}</span>
                    <strong title={view.senderName}>{view.senderName}</strong>
                  </div>
                </div>
                <div className="tdm-party">
                  <span className="tdm-party-icon tdm-party-icon--receiver" aria-hidden="true">
                    <i className={`fa-solid ${view.receiverIcon}`} />
                  </span>
                  <div className="tdm-party-copy">
                    <span>{view.receiverLabel}</span>
                    <strong title={view.receiverName}>{view.receiverName}</strong>
                  </div>
                </div>
              </div>

              {actions ? <div className="tdm-actions">{actions}</div> : null}

              <button type="button" className="tdm-help-btn" onClick={handleHelp}>
                <i className="fa-regular fa-circle-question" aria-hidden="true" />
                {t("need_help_with_this", { ns: "mobile", defaultValue: "Need help with this?" })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
